import {
  OnboardingMessage,
  MessageContext,
} from "@/lib/api/onboarding-messages";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Loader2,
  MessageCircle,
  Trash2,
  GripVertical,
  Pencil,
  Check,
  X,
} from "lucide-react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useState } from "react";
import { Textarea } from "@/components/ui/textarea";
import { useUpdateMessage } from "@/lib/hooks/onboarding-messages/use-onboarding-messages";

interface OnboardingMessagesListProps {
  messages: OnboardingMessage[];
  isLoading: boolean;
  userId: string;
  handleDeleteMessage: (id: string) => void;
  isOffline?: boolean;
}

function SortableMessage({
  message,
  handleDeleteMessage,
  userId,
  isOffline,
}: {
  message: OnboardingMessage;
  handleDeleteMessage: (id: string) => void;
  userId: string;
  isOffline?: boolean;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedMessage, setEditedMessage] = useState(message.message);
  const messageContext = isOffline
    ? MessageContext.OFFLINE
    : MessageContext.ONLINE;
  const updateMessage = useUpdateMessage(userId, messageContext);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: message.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleSave = () => {
    if (editedMessage.trim() && editedMessage !== message.message) {
      updateMessage.mutate({
        messageId: message.id,
        message: editedMessage,
      });
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditedMessage(message.message);
    setIsEditing(false);
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className="hover:bg-muted/50 group flex items-center gap-4 rounded-lg border p-3"
    >
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab touch-none hover:text-foreground/80"
      >
        <GripVertical className="text-muted-foreground h-5 w-5 shrink-0" />
      </button>

      <div className="min-w-0 flex-1">
        {isEditing ? (
          <div className="space-y-2">
            <Textarea
              value={editedMessage}
              onChange={(e) => setEditedMessage(e.target.value)}
              className="min-h-[60px]"
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <Button
                size="sm"
                variant="ghost"
                onClick={handleCancel}
                className="h-8"
              >
                <X className="mr-1 h-4 w-4" />
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleSave}
                className="h-8"
                disabled={
                  !editedMessage.trim() || editedMessage === message.message
                }
              >
                <Check className="mr-1 h-4 w-4" />
                Save
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <MessageCircle className="text-muted-foreground h-5 w-5 shrink-0" />
            <p className="flex-1 break-words text-sm">{message.message}</p>
          </div>
        )}
      </div>

      <div className="flex gap-2 opacity-0 transition-opacity group-hover:opacity-100">
        {!isEditing && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsEditing(true)}
            className="h-8 w-8"
          >
            <Pencil className="text-muted-foreground h-4 w-4" />
          </Button>
        )}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => handleDeleteMessage(message.id)}
          className="h-8 w-8"
        >
          <Trash2 className="h-4 w-4 text-destructive" />
        </Button>
      </div>
    </li>
  );
}

export function OnboardingMessagesList({
  messages,
  isLoading,
  userId,
  handleDeleteMessage,
  isOffline,
}: OnboardingMessagesListProps) {
  if (isLoading) {
    return (
      <div className="flex h-[200px] items-center justify-center">
        <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="mt-8 text-center">
        <MessageCircle className="text-muted-foreground/50 mx-auto h-12 w-12" />
        <h3 className="text-muted-foreground mt-2 text-sm font-medium">
          No messages
        </h3>
        <p className="text-muted-foreground/75 mt-1 text-sm">
          Add a message to get started
        </p>
      </div>
    );
  }

  return (
    <Card className="p-4">
      <ul className="space-y-3">
        {messages.map((message) => (
          <SortableMessage
            key={message.id}
            message={message}
            handleDeleteMessage={handleDeleteMessage}
            userId={userId}
            isOffline={isOffline}
          />
        ))}
      </ul>
    </Card>
  );
}
